// src/components/SortDropdown.tsx
import * as React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpDown, Shuffle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  SortOption,
  SORT_OPTIONS,
  SORT_OPTION_LABELS,
  DEFAULT_SORT_OPTION,
} from "@/lib/sortUtils";

export interface SortDropdownProps {
  value: SortOption;
  onChange: (value: SortOption) => void;
  onReshuffle?: () => void;
  disabled?: boolean;
  className?: string;
}

/**
 * SortDropdown component for selecting sort order in ad grids.
 *
 * Provides 4 sort options: Newest, Oldest, Longest Running, Most Impressions.
 * Uses shadcn/ui Select component with consistent dark theme styling.
 *
 * @example
 * ```tsx
 * const [sortOption, setSortOption] = useState<SortOption>('newest');
 * <SortDropdown value={sortOption} onChange={setSortOption} />
 * ```
 */
export const SortDropdown: React.FC<SortDropdownProps> = ({
  value,
  onChange,
  onReshuffle,
  disabled = false,
  className,
}) => {
  const handleValueChange = (newValue: string) => {
    // Type guard to ensure the value is a valid SortOption
    if (SORT_OPTIONS.includes(newValue as SortOption)) {
      onChange(newValue as SortOption);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Select value={value} onValueChange={handleValueChange} disabled={disabled}>
        <SelectTrigger
          className={cn(
            // Responsive sizing: full width on mobile, auto on desktop
            "w-full md:w-auto md:min-w-[180px]",
            // Height: taller on mobile for better touch targets (44px min)
            "h-11 md:h-10",
            "border border-gray-300",
            "bg-background text-sm font-medium",
            "hover:bg-accent",
            // Active state for touch feedback
            "active:bg-accent/80",
            "focus:outline-none focus:ring-0 focus:border-gray-300",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            // Touch optimization
            "touch-manipulation",
            className
          )}
        >
          <div className="flex flex-row items-center gap-2 w-full">
            <ArrowUpDown className="h-4 w-4 shrink-0 opacity-60" />
            <span className="truncate">{SORT_OPTION_LABELS[value]}</span>
          </div>
        </SelectTrigger>
      <SelectContent
        className={cn(
          "bg-popover border rounded-md shadow-md",
          // Ensure minimum width on mobile for readability
          "min-w-[180px]",
          // Prevent overflow on small screens
          "max-w-[calc(100vw-2rem)]"
        )}
        align="start"
        // Position dropdown below trigger with some offset on mobile
        sideOffset={4}
      >
        {SORT_OPTIONS.map((option) => (
          <SelectItem
            key={option}
            value={option}
            className={cn(
              "text-sm cursor-pointer",
              // Larger touch targets for mobile
              "min-h-[44px] py-3",
              "touch-manipulation"
            )}
          >
            {SORT_OPTION_LABELS[option]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    {value === "shuffle" && onReshuffle && (
      <button
        onClick={onReshuffle}
        disabled={disabled}
        className={cn(
          "h-10 w-10 flex items-center justify-center rounded-md border border-gray-300 hover:bg-accent touch-manipulation",
          "disabled:opacity-50 disabled:cursor-not-allowed"
        )}
        title="Reshuffle"
      >
        <Shuffle className="h-4 w-4" />
      </button>
    )}
    </div>
  );
};

// Re-export SortOption type and utilities for convenience
export type { SortOption };
export { SORT_OPTIONS, SORT_OPTION_LABELS, DEFAULT_SORT_OPTION };

export default SortDropdown;
